import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/input-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  CampoBuscaNfseFiltro,
  DireitoCreditoFiltro,
  FiltrosTabelaNfse,
} from './hooks/useServicosNfsePage';

interface FiltroServicosNfseControlsProps {
  filtros: FiltrosTabelaNfse;
  onChange: (filtros: FiltrosTabelaNfse) => void;
  onLimpar: () => void;
}

const DIREITO_CREDITO_OPTIONS: Array<{ value: DireitoCreditoFiltro; label: string }> = [
  { value: 'todos', label: 'Crédito' },
  { value: 'com-direito', label: 'Com Direito' },
  { value: 'sem-direito', label: 'Sem Direito' },
];

const CAMPO_BUSCA_OPTIONS: Array<{
  value: CampoBuscaNfseFiltro;
  label: string;
  placeholder: string;
}> = [
  { value: 'numeroNota', label: 'Número', placeholder: 'Buscar' },
  { value: 'chaveAcesso', label: 'Chave', placeholder: 'Buscar' },
  { value: 'prestador', label: 'Prestador', placeholder: 'Buscar' },
  { value: 'codigoServico', label: 'Cod. Serviço', placeholder: 'Buscar' },
];

export function FiltroServicosNfseControls({
  filtros,
  onChange,
  onLimpar,
}: FiltroServicosNfseControlsProps) {
  const [campoBusca, setCampoBusca] = useState(filtros.campoBusca);
  const [termoBusca, setTermoBusca] = useState(filtros.termoBusca);
  const [direitoCredito, setDireitoCredito] = useState(filtros.direitoCredito);
  const campoBuscaSelecionado =
    CAMPO_BUSCA_OPTIONS.find(option => option.value === campoBusca) || CAMPO_BUSCA_OPTIONS[0];

  useEffect(() => {
    if (filtros.termoBusca !== '') return;

    setCampoBusca(filtros.campoBusca);
    setTermoBusca(filtros.termoBusca);
    setDireitoCredito(filtros.direitoCredito);
  }, [filtros.campoBusca, filtros.direitoCredito, filtros.termoBusca]);

  useEffect(() => {
    if (termoBusca === filtros.termoBusca) return;

    const timeout = window.setTimeout(() => {
      onChange({ campoBusca, termoBusca, direitoCredito });
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [campoBusca, direitoCredito, filtros.termoBusca, onChange, termoBusca]);

  const handleCampoBuscaChange = (nextCampoBusca: CampoBuscaNfseFiltro) => {
    setCampoBusca(nextCampoBusca);
    onChange({ campoBusca: nextCampoBusca, termoBusca, direitoCredito });
  };

  const handleDireitoCreditoChange = (nextDireitoCredito: DireitoCreditoFiltro) => {
    setDireitoCredito(nextDireitoCredito);
    onChange({ campoBusca, termoBusca, direitoCredito: nextDireitoCredito });
  };

  const handleLimpar = () => {
    onLimpar();
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <InputGroup className="w-[340px] bg-background">
        <InputGroupAddon>
          <InputGroupText>
            <Search className="h-4 w-4" />
          </InputGroupText>
        </InputGroupAddon>
        <InputGroupInput
          placeholder={campoBuscaSelecionado.placeholder}
          value={termoBusca}
          onChange={event => setTermoBusca(event.target.value)}
        />
        <InputGroupAddon align="inline-end" className="h-full border-l px-0 py-0">
          <Select
            value={campoBusca}
            onValueChange={(value: CampoBuscaNfseFiltro) => handleCampoBuscaChange(value)}
          >
            <SelectTrigger
              aria-label="Campo da busca"
              className="h-8 w-[118px] rounded-none border-0 bg-transparent shadow-none focus:ring-0 focus:ring-offset-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CAMPO_BUSCA_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </InputGroupAddon>
      </InputGroup>

      <Select
        value={direitoCredito}
        onValueChange={(value: DireitoCreditoFiltro) => handleDireitoCreditoChange(value)}
      >
        <SelectTrigger aria-label="Direito a crédito" className="h-9 w-[128px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DIREITO_CREDITO_OPTIONS.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleLimpar}>
        <X className="h-4 w-4" />
        <span className="sr-only">Limpar filtros</span>
      </Button>
    </div>
  );
}
